import { Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useIsHalloweenSeason } from "@/hooks/useIsHalloweenSeason";
import { ANNIE_STUDIO, FONT_SIZE, INK, RADIUS } from "@/theme/tokens";
import type { AssistantAvatarConfig, AssistantAvatarMood } from "@/ui/AssistantAvatar/assistantAvatarConfig";
import { AnnieStudioBubble } from "./AnnieStudioBubble";
import { AnnieStudioMoodChips } from "./AnnieStudioMoodChips";
import { AnnieStudioPreview } from "./AnnieStudioPreview";
import { AnnieStudioChange } from "./useAnnieStudioDraft";

type Props = {
	draft: AssistantAvatarConfig;
	/** The draft name, so the preview answers to it as the user types. */
	name: string;
	mood: AssistantAvatarMood;
	onMoodChange: (mood: AssistantAvatarMood) => void;
	revision: number;
	moodRevision: number;
	lastChange: AnnieStudioChange;
	isHidden: boolean;
	/** Phone layout: a short row pinned above the pickers. */
	compact: boolean;
};

const BUBBLE_KEY: Record<AnnieStudioChange, string> = {
	[AnnieStudioChange.Opened]: "annie.studio.bubble.opened",
	[AnnieStudioChange.Preset]: "annie.studio.bubble.preset",
	[AnnieStudioChange.Style]: "annie.studio.bubble.style",
	[AnnieStudioChange.Part]: "annie.studio.bubble.part",
	[AnnieStudioChange.Surprise]: "annie.studio.bubble.surprise",
	[AnnieStudioChange.Reset]: "annie.studio.bubble.reset",
};

/** The Studio's focal preview: the draft, reacting to each pick, with the six moods to try. */
export const AnnieStudioStage: FC<Props> = ({ draft, name, mood, onMoodChange, revision, moodRevision, lastChange, isHidden, compact }) => {
	const theme = useTheme();
	const { t, i18n } = useTranslation();
	const primary = theme.palette.primary.main;
	const glowAt = compact ? (i18n.dir() === "rtl" ? "80% 50%" : "20% 50%") : "50% 44%";
	const surface = {
		background: `radial-gradient(circle at ${glowAt}, ${alpha(primary, ANNIE_STUDIO.WASH.CORE)} 0%, ${alpha(primary, ANNIE_STUDIO.WASH.EDGE)} 62%)`,
		border: `1px solid ${alpha(primary, ANNIE_STUDIO.STAGE_BORDER_ALPHA)}`,
	};

	// In Halloween week no saved design means the pumpkin, not the standard icon, so Reset says so.
	const isHalloweenReset = useIsHalloweenSeason() && lastChange === AnnieStudioChange.Reset;
	const bubbleKey = isHalloweenReset ? "annie.studio.bubble.reset_halloween" : BUBBLE_KEY[lastChange];
	const bubble = <AnnieStudioBubble text={isHidden ? t("annie.studio.bubble.hidden") : t(bubbleKey, { name })} compact={compact} />;
	const preview = (
		<AnnieStudioPreview
			draft={draft}
			mood={mood}
			revision={revision}
			moodRevision={moodRevision}
			size={compact ? ANNIE_STUDIO.STAGE_AVATAR_COMPACT : ANNIE_STUDIO.STAGE_AVATAR}
			isHidden={isHidden}
		/>
	);
	const moods = <AnnieStudioMoodChips mood={mood} onMoodChange={onMoodChange} disabled={isHidden} compact={compact} />;

	if (compact) {
		return (
			<Box data-testid="annie-studio-stage" sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, borderRadius: `${RADIUS.POPOVER}px`, ...surface }}>
				{preview}
				<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1, minWidth: 0 }}>
					{bubble}
					{moods}
				</Box>
			</Box>
		);
	}

	return (
		<Box
			data-testid="annie-studio-stage"
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				gap: 1.5,
				px: 2.5,
				py: 3,
				borderRadius: `${RADIUS.HERO}px`,
				...surface,
			}}
		>
			{bubble}
			{preview}
			<Typography data-testid="annie-studio-preview-name" sx={{ fontWeight: 800, fontSize: FONT_SIZE.TITLE_XL, mt: -0.5 }}>
				{name}
			</Typography>
			{moods}
			<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, textAlign: "center" }}>{t("annie.studio.moods.hint")}</Typography>
		</Box>
	);
};
